import { SheetHeader } from "@/design-system/BottomSheet/SheetHeader";
import { Dropdown } from "@/design-system/Dropdown/Dropdown";
import { Switch } from "@/design-system/Switch/Switch";
import { formationsAsListSelector } from "@/redux/loadedContent/loadedContentSlice";
import type { ObjectiveSquadSettings } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

export const DEFAULT_SQUAD_SETTINGS: ObjectiveSquadSettings = {
  includeLoanPlayers: true,
  includeActiveEvos: true,
  marketManagers: false,
  preferredFormation: null,
};

/** Customize Squads: which cards and managers the next squad build may use. Changes apply at once. */
export const SquadSettingsPanel = ({
  compact,
  close,
  settings,
  onChange,
}: {
  compact: boolean;
  close: () => void;
  settings: ObjectiveSquadSettings;
  onChange: (settings: ObjectiveSquadSettings) => void;
}) => {
  const { t } = useTranslation(["objectives", "common"]);
  const formations = useSelector(formationsAsListSelector);
  const formationItems = [
    { value: "", label: t("plan.squadSettings.noFormation") },
    ...formations
      .map((f) => ({
        value: f.id,
        label: f.label,
        // Typing "433" finds 4-3-3.
        searchText: f.label.replace(/-/g, ""),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const rows = [
    { key: "includeLoanPlayers", label: t("plan.squadSettings.loans") },
    { key: "includeActiveEvos", label: t("plan.squadSettings.evos") },
    // On means owned managers only, the opposite of the stored flag.
    {
      key: "marketManagers",
      label: t("plan.squadSettings.managers"),
      inverted: true,
    },
  ] as const;

  return (
    <div className="flex flex-col text-neutral-100">
      {compact ? (
        <SheetHeader
          title={t("plan.squadSettings.title")}
          onClose={close}
          closeLabel={t("common:close")}
        />
      ) : (
        // Right padding keeps the text clear of the popover's pinned close button.
        <h2 className="pl-4 pr-12 pt-4 text-[15px] font-semibold leading-[1.3] text-foreground">
          {t("plan.squadSettings.title")}
        </h2>
      )}
      <div className="flex flex-col gap-y-3 p-4">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between gap-x-4 text-sm"
          >
            <span>{row.label}</span>
            <Switch
              isActive={
                "inverted" in row ? !settings[row.key] : settings[row.key]
              }
              onChange={(value) =>
                onChange({
                  ...settings,
                  [row.key]: "inverted" in row ? !value : value,
                })
              }
            />
          </div>
        ))}
        <div className="flex items-center justify-between gap-x-4 text-sm">
          <span>{t("plan.squadSettings.formation")}</span>
          {/* Portalled, so the list can reach past the panel's edges. */}
          <Dropdown
            items={formationItems}
            variant="secondary"
            placeholder={t("plan.squadSettings.noFormation")}
            selected={settings.preferredFormation ?? ""}
            onChange={(item) =>
              onChange({ ...settings, preferredFormation: item.value || null })
            }
            triggerWidth={140}
            align="end"
            portal
          />
        </div>
      </div>
    </div>
  );
};
